import { ReactComponent as Download } from "assets/icons/download.svg";
import { ReactComponent as SaveTemplate } from "assets/icons/hard-drive-download.svg";
import { AcceptFileType, FileTypePurpose } from "common/acceptTypes";
import { getDropdownOptions, getDropdownValue } from "helpers/DropdownHelper";
import { DEFAULT_PAGE_SIZE_OPTION } from "helpers/PaginationHelper";
import {
    Button,
    DatePicker,
    Dropdown,
    Field,
    Input,
    Loading,
    NotificationPriorityTranslation,
    NotificationSendingEntityTranslation,
    NotificationSeverityTranslation,
    NotificationTypeTranslation,
    onChange,
    OptionType,
    TextArea,
    useAccessToken,
    useGetAllCounties,
    useGetUserAccountsCountQuery,
    UserAccountActiveStatus,
    validate,
    ValidationErrors,
    VARIABLE_DROPDOWN_OPTIONS,
} from "modules/common";
import { ProfessionalField, ProfessionalFieldFilterFields } from "modules/common/constants/professionalFieldTypes";
import { Accordion } from "modules/common/components/Accordion";
import { Dropzone } from "modules/common/components/Dropzone";
import {
    exportRecipients,
    useGetNotificationTemplate,
    useSendNotification,
} from "modules/communication";
import {
    NotificationUsersModal,
    SaveNotificationAsTemplateConfirmationModal,
    SendNotificationFilters,
} from "modules/communication/components";
import { NotificationStatistics } from "modules/communication/components/NotificationStatistics";
import { SendNotificationConfirmationModal } from "modules/communication/components/SendNotificationConfirmationModal";
import { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { AppRoutes } from "routing";
import { ExportNotificationRecipientsFileType } from "services";
import { Notification } from "services/communication";
import { AccessibilityContext } from "./AccessibilityContext";
import { handleKeyDown } from "modules/common/utils/keyboardUtils";

const containsVariable = (value?: string) => {
    if (!value) return;
    return Object.entries(VARIABLE_DROPDOWN_OPTIONS).some((_, i) =>
        value.includes(`{{${VARIABLE_DROPDOWN_OPTIONS[i].value}}}`)
    );
};

const ERROR_MESSAGES: { [P in keyof Notification]?: string } = {
    subject: "Naslov je obavezan",
    body: "Poruka je obavezna",
    type: "Način slanja je obavezan",
    severity: "Vrsta poruke je obavezna",
    priority: "Prioritet je obavezan",
    professionalField: "Stručno područje je obavezno",
    sendingEntity: "Pošiljatelj je obavezan",
};

const FIELD_ID: { [P in keyof Notification]?: string } = {
    subject: "subject",
    body: "body",
    type: "type",
    severity: "severity",
    priority: "priority",
    professionalField: "professionalField",
    sendingEntity: "sendingEntity",
};

export const SendNotification = () => {
    const location = useLocation();
    const accessToken = useAccessToken();
    const history = useHistory();

    const [isSendingNotification, setIsSendingNotification] = useState(false);

    // notification form
    const [errors, setErrors] = useState<ValidationErrors<Notification>>({});
    const [notification, setNotification] = useState<Partial<Notification>>({});
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

    // user filters
    const [registryFilter, setRegistryFilter] = useState<string[]>([]);
    const [userRoleFilter, setUserRoleFilter] = useState<number[]>([]);
    const [countyFilter, setCountyFilter] = useState<number[]>([]);
    const [citiesMunicipalityFilter, setCitiesMunicipalityFilter] = useState<number[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE_OPTION);

    const [notificationTemplateId, setNotificationTemplateId] = useState<string | null>(null);
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

    const { data: countyOptions, isFetching: isLoadingCounties } = useGetAllCounties();

    const countyOptionsMemo = useMemo(() => {
        return countyOptions?.map<OptionType<number | null>>(c => ({ label: c.name, value: c.id }));
    }, [countyOptions]);

    const ProfessionalFieldOptions = Object.fromEntries(
        Object.entries(ProfessionalFieldFilterFields)
            .filter(([, { value }]) => value !== ProfessionalField.None)
            .map(([, { value, label }]) => [label, label])
    ) as Record<string, string>;

    // user count
    const {
        data: recipientsCount,
        isFetching: isLoadingRecipientCount,
        refetch: refetchRecipientsCount,
    } = useGetUserAccountsCountQuery(
        {
            statusFilter: [UserAccountActiveStatus.Active],
            registryFilter: registryFilter,
            userRoleFilter: userRoleFilter,
            countyFilter: countyFilter,
            citiesMunicipalityFilter: citiesMunicipalityFilter,
        },
        false
    );

    // clear selected users when filters change and users modal is not opened
    useEffect(() => {
        if (selectedUsers.length === 0) refetchRecipientsCount();

        if (!isNotificationUsersModalOpen) setSelectedUsers([]);
    }, [registryFilter, userRoleFilter, countyFilter, citiesMunicipalityFilter]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        setNotificationTemplateId(params.get("templateId"));
    }, [location.search]);

    // modals
    const [isNotificationUsersModalOpen, setIsNotificationUsersModalOpen] = useState(false);
    const [isCreateTemplateModalOpen, setIsCreateTemplateModalOpen] = useState(false);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

    // mutation
    const sendNotification = useSendNotification();

    const validation = () => {
        const errors = validate(notification, ERROR_MESSAGES);
        if (notification.subject && notification.subject.length > 150)
            errors.subject = "Naslov poruke ne smije sadržavati više od 150 znakova";

        setErrors(errors);

        if (errors)
            document
                .getElementById(FIELD_ID[Object.keys(errors)[0] as keyof typeof FIELD_ID] ?? "")
                ?.scrollIntoView({ behavior: "smooth" });

        return errors;
    };

    const onSubmit = async () => {
        if (Object.keys(validation()).length !== 0) return;

        setIsSendingNotification(p => !p);
        await sendNotification
            .mutateAsync({
                ...(notification as Notification),
                attachments: uploadedFiles,
                recipientUids: selectedUsers,
                userFilter: {
                    countyFilter: countyFilter,
                    registryFilter: registryFilter,
                    userRoleFilter: userRoleFilter,
                    citiesMunicipalityFilter: citiesMunicipalityFilter,
                },
            })
            .then(_ => {
                setSelectedUsers([]);
                resetForm();
                setIsSendingNotification(false);
            })
            .catch(_ => setIsSendingNotification(false));
    };

    const resetForm = () => {
        setNotification({});
        setErrors({});
        setUploadedFiles([]);
    };

    const handleDrop = useMemo(
        () => (files: File[]) => setUploadedFiles(prev => [...prev, ...files]),
        []
    );
    const handleDelete = useMemo(
        () => (file: File) =>
            setUploadedFiles(prev => prev.filter(existingFile => existingFile.name !== file.name)),
        []
    );

    const exportNotificationRecipients = async (
        fileExtension: ExportNotificationRecipientsFileType
    ) => {
        exportRecipients(
            {
                recipientUids: selectedUsers,
                fileExtension,
                userFilter: {
                    registryFilter: registryFilter,
                    userRoleFilter: userRoleFilter,
                    countyFilter: countyFilter,
                    citiesMunicipalityFilter: citiesMunicipalityFilter,
                },
            },
            accessToken
        );
    };

    const { data: notificationTemplate, isFetching: isLoadingNotificationTemplate } =
        useGetNotificationTemplate(notificationTemplateId!, Boolean(notificationTemplateId));

    const { lowVision } = useContext(AccessibilityContext);

    useEffect(() => {
        if (notificationTemplate && notificationTemplateId) {
            onChange({ ...notificationTemplate }, setNotification, setErrors);
        } else resetForm();
    }, [notificationTemplate, notificationTemplateId]);

    return (
        <>
            <div className="f-c g-24 f-1">
                <SendNotificationFilters
                    counties={countyOptionsMemo}
                    isLoadingCounties={isLoadingCounties}
                    countyFilter={countyFilter}
                    setCountyFilter={setCountyFilter}
                    settlementFilter={citiesMunicipalityFilter}
                    setCityFilter={setCitiesMunicipalityFilter}
                    roleFilter={userRoleFilter}
                    registryFilter={registryFilter}
                    setRegistryFilter={setRegistryFilter}
                    setRoleFilter={setUserRoleFilter}
                    setCurrentPage={setCurrentPage}
                />

                {isLoadingNotificationTemplate || isSendingNotification ? (
                    <Loading />
                ) : (
                    <>
                        <div className="communication-recipients g-24 f-r">
                            <Button
                                className="f-50"
                                size="small"
                                type="button"
                                variant="primary"
                                lowVision={lowVision}
                                onClick={() => setIsNotificationUsersModalOpen(prev => !prev)}
                            >
                                LISTA PRIMATELJA
                            </Button>
                            <div className="f-50">
                                Standardno su odabrani svi korisnici sa liste. Ukoliko želite
                                poslati poruku samo određenom skupu korisnika potrebno je unutar
                                liste to definirati.
                            </div>
                        </div>

                        <Accordion
                            title="UKUPAN BROJ PRIMATELJA"
                            afterTitleElement={
                                isLoadingRecipientCount ? (
                                    <Loading size="small" />
                                ) : (
                                    selectedUsers.length || recipientsCount
                                )
                            }
                            isBackground
                        >
                            <NotificationStatistics
                                userFilter={{
                                    registryFilter: registryFilter,
                                    userRoleFilter: userRoleFilter,
                                    countyFilter: countyFilter,
                                    citiesMunicipalityFilter: citiesMunicipalityFilter,
                                }}
                                selectedUsers={selectedUsers}
                            />
                        </Accordion>

                        <div className="f-r even g-24">
                            <Button
                                size="small"
                                onClick={() => exportNotificationRecipients("csv")}
                                variant="primary-ghost"
                                lowVision={lowVision}
                            >
                                <span
                                    tabIndex={0}
                                    onKeyDown={e =>
                                        handleKeyDown(e, () => exportNotificationRecipients("csv"))
                                    }
                                >
                                    IZVOZ POPISA PRIMATELJA .CSV <Download />
                                </span>
                            </Button>

                            <Button
                                size="small"
                                onClick={() => exportNotificationRecipients("xlsx")}
                                variant="primary-ghost"
                                lowVision={lowVision}
                            >
                                <span
                                    tabIndex={0}
                                    onKeyDown={e =>
                                        handleKeyDown(e, () => exportNotificationRecipients("xlsx"))
                                    }
                                >
                                    IZVOZ POPISA PRIMATELJA .XLSX <Download />
                                </span>
                            </Button>
                        </div>

                        <hr className="separator mt-12 mb-12" />

                        <div className="communication-notification even">
                            <Field
                                id={FIELD_ID.subject}
                                name="NASLOV PORUKE"
                                required
                                errorMessage={errors.subject}
                            >
                                <Input
                                    isValid={!errors.subject}
                                    value={notification.subject ?? ""}
                                    onChange={e =>
                                        onChange(
                                            { subject: e.target.value },
                                            setNotification,
                                            setErrors
                                        )
                                    }
                                    id="subject"
                                    name="subject"
                                    size="medium"
                                    type="text"
                                    placeholder="Tekst naslova"
                                />
                            </Field>

                            <Field
                                id={FIELD_ID.body}
                                name="PORUKA"
                                required
                                errorMessage={errors.body}
                            >
                                <>
                                    <TextArea
                                        height={200}
                                        placeholder="Tekst poruke"
                                        useVariables
                                        onChange={e =>
                                            onChange({ body: e }, setNotification, setErrors)
                                        }
                                        name="body"
                                        value={notification?.body}
                                    />
                                    {containsVariable(notification.body) && (
                                        <span>
                                            U tekstu koristite parametre koji možda nisu definirani
                                            za sve korisnike.
                                        </span>
                                    )}
                                </>
                            </Field>

                            <Field
                                id={FIELD_ID.type}
                                name="NAČIN SLANJA"
                                required
                                errorMessage={errors.type}
                            >
                                <Dropdown
                                    value={getDropdownValue(
                                        notification.type,
                                        NotificationTypeTranslation
                                    )}
                                    onChange={item =>
                                        onChange({ type: item?.value }, setNotification, setErrors)
                                    }
                                    options={getDropdownOptions(NotificationTypeTranslation)}
                                    placeholder="Odaberite način slanja"
                                    maxMenuHeight={200}
                                    size="medium"
                                />
                            </Field>

                            <Field name="Dodaj datoteku">
                                <Dropzone
                                    handleDrop={handleDrop}
                                    handleDelete={handleDelete}
                                    files={uploadedFiles}
                                    isMultiple
                                    accept={AcceptFileType[FileTypePurpose.COMMUNICATION]}
                                />
                            </Field>
                        </div>

                        <hr className="separator mt-12 mb-12" />

                        <div className="f-c g-12">
                            <Field horizontal name="DATUM SLANJA">
                                <DatePicker
                                    placeholder="(Automatsko popunjavanje)"
                                    displayMonth={new Date()}
                                    minDate={new Date()}
                                    onClick={(day: Date | null) =>
                                        onChange(
                                            { scheduledAt: day ?? undefined },
                                            setNotification,
                                            setErrors
                                        )
                                    }
                                    value={notification.scheduledAt}
                                    selectedDay={notification?.scheduledAt ?? null}
                                />
                            </Field>

                            <Field
                                id={FIELD_ID.severity}
                                name="VRSTA PORUKE"
                                horizontal
                                required
                                errorMessage={errors.severity}
                            >
                                <Dropdown
                                    value={getDropdownValue(
                                        notification.severity,
                                        NotificationSeverityTranslation
                                    )}
                                    onChange={item =>
                                        onChange(
                                            { severity: item?.value },
                                            setNotification,
                                            setErrors
                                        )
                                    }
                                    options={getDropdownOptions(NotificationSeverityTranslation)}
                                    placeholder="Odaberite vrstu"
                                    maxMenuHeight={200}
                                    size="medium"
                                />
                            </Field>

                            <Field
                                id={FIELD_ID.priority}
                                name="PRIORITET PORUKE"
                                horizontal
                                required
                                errorMessage={errors.priority}
                            >
                                <Dropdown
                                    value={getDropdownValue(
                                        notification.priority,
                                        NotificationPriorityTranslation
                                    )}
                                    onChange={item =>
                                        onChange(
                                            { priority: item?.value },
                                            setNotification,
                                            setErrors
                                        )
                                    }
                                    options={getDropdownOptions(NotificationPriorityTranslation)}
                                    placeholder="Odaberite prioritet"
                                    maxMenuHeight={200}
                                    size="medium"
                                />
                            </Field>

                            <Field
                                id={FIELD_ID.professionalField}
                                name="STRUČNO PODRUČJE"
                                horizontal
                                required
                                errorMessage={errors.professionalField}
                            >
                                <Dropdown
                                    placeholder="Odaberite stručno područje"
                                    value={getDropdownValue(
                                        notification?.professionalField,
                                        ProfessionalFieldOptions
                                    )}
                                    onChange={item =>
                                        onChange(
                                            { professionalField: item?.label },
                                            setNotification,
                                            setErrors
                                        )
                                    }
                                    maxMenuHeight={200}
                                    size="medium"
                                    options={getDropdownOptions(ProfessionalFieldOptions)}
                                />
                            </Field>

                            <Field
                                id={FIELD_ID.sendingEntity}
                                name="POŠILJATELJ"
                                horizontal
                                required
                                errorMessage={errors.sendingEntity}
                            >
                                <Dropdown
                                    value={getDropdownValue(
                                        notification.sendingEntity,
                                        NotificationSendingEntityTranslation
                                    )}
                                    onChange={item =>
                                        onChange(
                                            { sendingEntity: item?.value },
                                            setNotification,
                                            setErrors
                                        )
                                    }
                                    options={getDropdownOptions(
                                        NotificationSendingEntityTranslation
                                    )}
                                    placeholder="Odaberite pošiljatelja"
                                    maxMenuHeight={200}
                                    size="medium"
                                />
                            </Field>
                        </div>

                        <hr className="separator mt-12 mb-12" />

                        <div className="communication-action-buttons f-r even g-32">
                            <Button
                                size="small"
                                onClick={() => {
                                    history.push(AppRoutes.Communication);
                                    resetForm();
                                }}
                                variant="primary-ghost"
                                lowVision={lowVision}
                            >
                                ODUSTANI
                            </Button>

                            <Button
                                size="small"
                                onClick={() =>
                                    Object.keys(validation()).length === 0 &&
                                    setIsCreateTemplateModalOpen(true)
                                }
                                variant="primary-ghost"
                                lowVision={lowVision}
                            >
                                <span>
                                    <SaveTemplate /> SPREMI PREDLOŽAK
                                </span>
                            </Button>

                            <Button
                                size="small"
                                onClick={() =>
                                    Object.keys(validation()).length === 0 &&
                                    setIsConfirmationModalOpen(true)
                                }
                                variant="primary"
                                lowVision={lowVision}
                            >
                                POŠALJI PORUKU
                            </Button>
                        </div>
                    </>
                )}
            </div>

            <NotificationUsersModal
                isActive={isNotificationUsersModalOpen}
                toggleModal={() => setIsNotificationUsersModalOpen(false)}
                selectedUsers={selectedUsers}
                setSelectedUsers={setSelectedUsers}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                roleFilter={userRoleFilter}
                setRoleFilter={setUserRoleFilter}
                countyFilter={countyFilter}
                setCountyFilter={setCountyFilter}
                cityFilter={citiesMunicipalityFilter}
                setCityFilter={setCitiesMunicipalityFilter}
                registryFilter={registryFilter}
                setRegistryFilter={setRegistryFilter}
                counties={countyOptionsMemo}
                isLoadingCounties={isLoadingCounties}
            />

            <SendNotificationConfirmationModal
                isActive={isConfirmationModalOpen}
                toggleModal={() => setIsConfirmationModalOpen(false)}
                onConfirm={onSubmit}
                openRecipientList={() => setIsNotificationUsersModalOpen(true)}
                selectedUsersCount={selectedUsers.length}
                usersFilter={{
                    registryFilter: registryFilter,
                    userRoleFilter: userRoleFilter,
                    countyFilter: countyFilter,
                    citiesMunicipalityFilter: citiesMunicipalityFilter,
                }}
            />

            <SaveNotificationAsTemplateConfirmationModal
                isActive={isCreateTemplateModalOpen}
                toggleModal={() => setIsCreateTemplateModalOpen(false)}
                templateData={{
                    body: notification.body,
                    subject: notification.subject,
                    severity: notification.severity,
                    sendingEntity: notification.sendingEntity,
                    priority: notification.priority!,
                    professionalField: notification.professionalField,
                    type: notification.type,
                    isShared: false,
                }}
            />
        </>
    );
};
